import { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "../components/ui"
import { listPaidMediaSetups, discoverTaggingEvents } from "../api/gtmManager"
import { GA4_STANDARD_DIMENSION_SUGGESTIONS } from "../constants/ga4Dimensions"
import useGa4Property from "../hooks/useGa4Property"
import { useOrganization } from "../OrganizationContext"
import { supabase } from "../supabaseClient"
import { useUser } from "../UserContext"
import {
  buildConfigurationFindings,
  buildEventInventory,
  buildParameterInventory,
  ga4AuditCollection,
} from "../utils/ga4ConfigurationAudit"
import { getValidAccessToken } from "../utils/ga4Tokens"
import "./GA4ConfigurationAudit.css"

const AUDIT_TABS = [
  { id: "findings", label: "Findings" },
  { id: "settings", label: "Settings & streams" },
  { id: "events", label: "Events" },
  { id: "parameters", label: "Parameters" },
  { id: "rules", label: "Key events & rules" },
  { id: "links", label: "Product links" },
]

const LINK_SECTIONS = [
  ["googleAdsLinks", "Google Ads", "googleAdsLinks"],
  ["displayVideo360AdvertiserLinks", "Display & Video 360", "displayVideo360AdvertiserLinks"],
  ["searchAds360Links", "Search Ads 360", "searchAds360Links"],
  ["bigQueryLinks", "BigQuery", "bigQueryLinks"],
  ["firebaseLinks", "Firebase", "firebaseLinks"],
]

const formatLabel = (value) =>
  String(value || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase())

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—"
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

const formatNumber = (value) => Number(value || 0).toLocaleString()

const humanizeEnum = (value) =>
  value ? formatLabel(String(value).replace(/^GOOGLE_SIGNALS_/, "")) : "—"

const invokeGa4 = async (type, propertyId, accessToken, extra = {}) => {
  const { data, error } = await supabase.functions.invoke("ga4-proxy", {
    body: { type, propertyId, ...extra },
    headers: { "x-ga4-authorization": `Bearer ${accessToken}` },
  })
  if (error) {
    let message = error?.message || `Unable to run ${type}`
    try {
      const payload = error.context?.json ? await error.context.json() : null
      message = payload?.error || payload?.message || message
    } catch (_) {
      // Keep the Supabase function error message.
    }
    throw new Error(message)
  }
  if (data?.error) throw new Error(data.error)
  return data || {}
}

function EmptyState({ children }) {
  return <p className="ga4-config-audit__empty">{children}</p>
}

function ChipList({ values, empty = "—" }) {
  const list = Array.from(new Set((values || []).filter(Boolean)))
  if (!list.length) return <span className="ga4-config-audit__muted">{empty}</span>
  return (
    <div className="ga4-config-audit__chips">
      {list.map((value) => <span className="ga4-config-audit__chip" key={value}>{value}</span>)}
    </div>
  )
}

function SettingsTable({ rows }) {
  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Setting</TableHeaderCell>
            <TableHeaderCell>Current value</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(([label, value]) => (
            <TableRow key={label}>
              <TableCell><strong>{label}</strong></TableCell>
              <TableCell>{formatValue(value)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function GenericResourceTable({ rows }) {
  if (!rows.length) return <EmptyState>No configuration found.</EmptyState>
  const keys = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}))))
    .filter((key) => !["name", "createTime", "updateTime"].includes(key))
  return (
    <TableContainer className="ui-table-container--scroll">
      <Table className="ui-table--sticky">
        <TableHead>
          <TableRow>
            {keys.map((key) => <TableHeaderCell key={key}>{formatLabel(key)}</TableHeaderCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row?.name || index}>
              {keys.map((key) => <TableCell key={key}>{formatValue(row?.[key])}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function GA4ConfigurationAudit() {
  const user = useUser()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { selectedOrgId } = useOrganization()
  const propertyId = new URLSearchParams(search).get("id")
  const normalizedPropertyId = propertyId?.replace(/^properties\//, "") || ""
  const { property, loading: propertyLoading, error: propertyError } =
    useGa4Property(propertyId)
  const [activeTab, setActiveTab] = useState("findings")
  const [dateRangeDays, setDateRangeDays] = useState(90)
  const [extendedAudit, setExtendedAudit] = useState({})
  const [keyEventData, setKeyEventData] = useState({})
  const [eventResponse, setEventResponse] = useState({})
  const [gtmData, setGtmData] = useState({ setup: null, connection: null, events: [] })
  const [loading, setLoading] = useState(false)
  const [gtmLoading, setGtmLoading] = useState(false)
  const [error, setError] = useState("")
  const [lastRunAt, setLastRunAt] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [eventFilter, setEventFilter] = useState("all")
  const [showStandardParameters, setShowStandardParameters] = useState(false)

  const loadGtmData = useCallback(async () => {
    if (!selectedOrgId || !property?.id) return
    try {
      const data = await listPaidMediaSetups(selectedOrgId)
      const setup = (data.setups || []).find((item) => item.ga4_property_id === property.id) || null
      const connection = (data.gtm_connections || []).find(
        (item) => item.id === setup?.gtm_container_connection_id,
      ) || null
      const events = setup
        ? (data.tagging_events || []).filter((item) => item.setup_id === setup.id)
        : []
      setGtmData({ setup, connection, events })
    } catch (_) {
      setGtmData({ setup: null, connection: null, events: [] })
    }
  }, [property?.id, selectedOrgId])

  useEffect(() => {
    loadGtmData()
  }, [loadGtmData])

  useEffect(() => {
    if (!property?.id) return
    let cancelled = false
    const loadSavedAudit = async () => {
      const [settingsResult, eventsResult] = await Promise.all([
        supabase
          .from("ga4_property_settings")
          .select("settings, created_at")
          .eq("ga4_property_id", property.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("ga4_property_key_events")
          .select("events, created_at")
          .eq("ga4_property_id", property.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ])
      if (cancelled) return
      if (settingsResult.data?.settings) {
        setExtendedAudit(settingsResult.data.settings)
        setLastRunAt(settingsResult.data.created_at || null)
      }
      if (eventsResult.data?.events) setKeyEventData(eventsResult.data.events)
    }
    loadSavedAudit()
    return () => { cancelled = true }
  }, [property?.id])

  const runLiveAudit = useCallback(async () => {
    if (!user || !normalizedPropertyId) return
    setLoading(true)
    setError("")
    try {
      const accessToken = await getValidAccessToken(supabase, user)
      if (!accessToken) throw new Error("Connect a GA4 account before running a live audit.")
      const [settings, keyEvents, events] = await Promise.all([
        invokeGa4("extended-audit", normalizedPropertyId, accessToken),
        invokeGa4("key-events", normalizedPropertyId, accessToken),
        invokeGa4("event-inventory", normalizedPropertyId, accessToken, { dateRangeDays }),
      ])
      setExtendedAudit(settings)
      setKeyEventData(keyEvents)
      setEventResponse(events)
      setLastRunAt(new Date().toISOString())
    } catch (auditError) {
      setError(auditError?.message || "Unable to run the GA4 configuration audit.")
    } finally {
      setLoading(false)
    }
  }, [dateRangeDays, normalizedPropertyId, user])

  useEffect(() => {
    if (property?.id && user) runLiveAudit()
  }, [property?.id, runLiveAudit, user])

  const refreshGtm = async () => {
    if (!gtmData.setup || !selectedOrgId) return
    setGtmLoading(true)
    setError("")
    try {
      const result = await discoverTaggingEvents(selectedOrgId, gtmData.setup.id)
      setGtmData((current) => ({ ...current, events: result.events || [] }))
    } catch (gtmError) {
      setError(gtmError?.message || "Unable to refresh the GTM inventory.")
    } finally {
      setGtmLoading(false)
    }
  }

  const eventInventory = useMemo(
    () => buildEventInventory({
      eventReport: eventResponse.report || {},
      keyEventData,
      taggingEvents: gtmData.events,
    }),
    [eventResponse.report, gtmData.events, keyEventData],
  )
  const parameterInventory = useMemo(
    () => buildParameterInventory({ extendedAudit, keyEventData, taggingEvents: gtmData.events }),
    [extendedAudit, gtmData.events, keyEventData],
  )
  const findings = useMemo(
    () => buildConfigurationFindings({
      extendedAudit,
      eventInventory,
      parameterInventory,
      hasGtmSetup: Boolean(gtmData.setup),
    }),
    [eventInventory, extendedAudit, gtmData.setup, parameterInventory],
  )

  const streams = ga4AuditCollection(extendedAudit, "dataStreams")
  const enhancedByStream = new Map(
    (extendedAudit.enhancedMeasurementSettings || []).map((entry) => [entry.dataStream, entry.settings]),
  )
  const settings = extendedAudit.settings || extendedAudit || {}
  const filteredEvents = eventInventory.filter((event) => {
    const needle = searchTerm.trim().toLowerCase()
    if (needle && ![event.eventName, ...event.parameters].join(" ").toLowerCase().includes(needle)) return false
    if (eventFilter === "key") return event.isKeyEvent
    if (eventFilter === "gtm") return event.configuredInGtm
    if (eventFilter === "dormant") return !event.observedInGa4
    return true
  })
  const filteredParameters = parameterInventory.filter((parameter) => {
    const needle = searchTerm.trim().toLowerCase()
    return !needle || [
      parameter.parameterName,
      ...parameter.displayNames,
      ...parameter.events,
      ...parameter.sources,
    ].join(" ").toLowerCase().includes(needle)
  })
  const filteredStandardParameters = GA4_STANDARD_DIMENSION_SUGGESTIONS.filter((parameter) => {
    const needle = searchTerm.trim().toLowerCase()
    return !needle || `${parameter.parameterName} ${parameter.displayName} ${parameter.description}`
      .toLowerCase().includes(needle)
  })

  const keyEvents = ga4AuditCollection(keyEventData, "conversionEvents")
  const createRules = keyEventData.eventCreateRules || []
  const editRules = keyEventData.eventEditRules || []
  const criticalCount = findings.filter((finding) => finding.severity === "error").length
  const warningCount = findings.filter((finding) => finding.severity === "warning").length
  const totalEvents = eventInventory.reduce((sum, event) => sum + event.eventCount, 0)

  const renderFindings = () => (
    <div className="ui-stack ui-stack--lg">
      <div className="ga4-config-audit__metrics">
        <div><span>Critical</span><strong>{criticalCount}</strong></div>
        <div><span>Warnings</span><strong>{warningCount}</strong></div>
        <div><span>Observed events</span><strong>{formatNumber(eventInventory.filter((event) => event.observedInGa4).length)}</strong></div>
        <div><span>Event volume</span><strong>{formatNumber(totalEvents)}</strong></div>
        <div><span>Parameters</span><strong>{formatNumber(parameterInventory.length)}</strong></div>
        <div><span>Web streams</span><strong>{formatNumber(streams.filter((stream) => stream.type === "WEB_DATA_STREAM" || stream.webStreamData).length)}</strong></div>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Prioritized audit findings</CardTitle>
          <CardDescription>Configuration and collection risks are ordered by severity.</CardDescription>
        </CardHeader>
        <CardContent>
          {!findings.length ? (
            <EmptyState>No issues found in the settings and data available to this audit.</EmptyState>
          ) : (
            <div className="ga4-config-audit__findings">
              {findings.map((finding, index) => (
                <article className={`ga4-config-audit__finding is-${finding.severity}`} key={`${finding.area}-${finding.title}-${index}`}>
                  <span className="ga4-config-audit__finding-level">{finding.severity === "error" ? "Critical" : finding.severity}</span>
                  <div>
                    <strong>{finding.title}</strong>
                    <p>{finding.detail}</p>
                  </div>
                  <span>{finding.area}</span>
                </article>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )

  const renderSettings = () => (
    <div className="ui-stack ui-stack--lg">
      <Card>
        <CardHeader>
          <CardTitle>Property settings</CardTitle>
          <CardDescription>Identity, localization, retention, attribution, and privacy-related collection settings.</CardDescription>
        </CardHeader>
        <CardContent className="ga4-config-audit__two-column">
          <SettingsTable rows={[
            ["Property name", settings.displayName || property?.property_name],
            ["Property ID", normalizedPropertyId],
            ["Property type", settings.propertyType],
            ["Time zone", settings.timeZone],
            ["Currency", settings.currencyCode],
            ["Industry", humanizeEnum(settings.industryCategory)],
            ["Service level", settings.serviceLevel],
          ]} />
          <SettingsTable rows={[
            ["Event retention", humanizeEnum(extendedAudit.dataRetentionSettings?.eventDataRetention)],
            ["User retention", humanizeEnum(extendedAudit.dataRetentionSettings?.userDataRetention)],
            ["Reset retention on activity", extendedAudit.dataRetentionSettings?.resetUserDataOnNewActivity],
            ["Google Signals", humanizeEnum(extendedAudit.googleSignalsSettings?.state)],
            ["Reporting identity", humanizeEnum(extendedAudit.reportingIdentity?.reportingIdentity)],
            ["Acquisition model", humanizeEnum(extendedAudit.attributionSettings?.acquisitionConversionEventLookbackWindow)],
            ["Other model", humanizeEnum(extendedAudit.attributionSettings?.otherConversionEventLookbackWindow)],
          ]} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Data streams and collection</CardTitle>
          <CardDescription>Every GA4 stream, destination ID, site/app identity, and enhanced measurement control.</CardDescription>
        </CardHeader>
        <CardContent className="ui-stack ui-stack--lg">
          {!streams.length ? <EmptyState>No data streams found.</EmptyState> : streams.map((stream, index) => {
            const web = stream.webStreamData || {}
            const streamSettings = enhancedByStream.get(stream.name) || {}
            return (
              <section className="ga4-config-audit__stream" key={stream.name || index}>
                <div className="ga4-config-audit__section-heading">
                  <div><strong>{stream.displayName || `Stream ${index + 1}`}</strong><span>{stream.type || "Unknown stream type"}</span></div>
                  <Badge variant="outline">{web.measurementId || stream.name?.split("/").pop() || "No ID"}</Badge>
                </div>
                <SettingsTable rows={[
                  ["Default URL", web.defaultUri],
                  ["Measurement ID", web.measurementId],
                  ["Stream ID", stream.name?.split("/").pop()],
                  ["Firebase app ID", web.firebaseAppId || stream.iosAppStreamData?.firebaseAppId || stream.androidAppStreamData?.firebaseAppId],
                  ["Created", stream.createTime],
                  ["Updated", stream.updateTime],
                ]} />
                {Object.keys(streamSettings).length > 0 && (
                  <div className="ga4-config-audit__enhanced-grid">
                    {Object.entries(streamSettings)
                      .filter(([key]) => !["name", "uriQueryParameter"].includes(key))
                      .map(([key, value]) => (
                        <div key={key}><span>{formatLabel(key)}</span><strong>{formatValue(value)}</strong></div>
                      ))}
                  </div>
                )}
              </section>
            )
          })}
        </CardContent>
      </Card>
    </div>
  )

  const renderEvents = () => (
    <Card>
      <CardHeader>
        <CardTitle>All events</CardTitle>
        <CardDescription>
          Observed GA4 activity for the last {dateRangeDays} days, joined to key-event and GTM configuration.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!filteredEvents.length ? <EmptyState>No events match the current filters.</EmptyState> : (
          <TableContainer className="ui-table-container--scroll">
            <Table className="ui-table--sticky">
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Event</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell align="right">Event count</TableHeaderCell>
                  <TableHeaderCell align="right">Users</TableHeaderCell>
                  <TableHeaderCell align="right">Key events</TableHeaderCell>
                  <TableHeaderCell>Parameters</TableHeaderCell>
                  <TableHeaderCell>GTM source</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow key={event.eventName}>
                    <TableCell><strong>{event.eventName}</strong></TableCell>
                    <TableCell><div className="ga4-config-audit__chips">
                      {event.observedInGa4 && <Badge variant="accent">Observed</Badge>}
                      {event.isKeyEvent && <Badge variant="outline">Key event</Badge>}
                      {event.configuredInGtm && <Badge variant="subtle">GTM</Badge>}
                      {!event.observedInGa4 && <span className="ga4-config-audit__status is-dormant">Dormant</span>}
                    </div></TableCell>
                    <TableCell align="right">{formatNumber(event.eventCount)}</TableCell>
                    <TableCell align="right">{formatNumber(event.totalUsers)}</TableCell>
                    <TableCell align="right">{formatNumber(event.keyEventCount)}</TableCell>
                    <TableCell><ChipList values={event.parameters} empty="No configured parameters found" /></TableCell>
                    <TableCell>
                      {event.configuredInGtm ? (
                        <span>Tag {event.gtmTagId || "—"}<br /><small>Triggers: {event.gtmTriggerIds.join(", ") || "—"}</small></span>
                      ) : <span className="ga4-config-audit__muted">Not found in linked GTM data</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  )

  const renderParameters = () => (
    <div className="ui-stack ui-stack--lg">
      <Card>
        <CardHeader>
          <CardTitle>Collected and configured parameters</CardTitle>
          <CardDescription>
            Registered GA4 custom definitions, parameters configured in GTM, and parameters changed by GA4 rules. Unregistered parameters cannot be enumerated from GA4 itself.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!filteredParameters.length ? <EmptyState>No configured or registered parameters found.</EmptyState> : (
            <TableContainer className="ui-table-container--scroll">
              <Table className="ui-table--sticky">
                <TableHead><TableRow>
                  <TableHeaderCell>Parameter</TableHeaderCell>
                  <TableHeaderCell>GA4 definition</TableHeaderCell>
                  <TableHeaderCell>Scope / type</TableHeaderCell>
                  <TableHeaderCell>Events</TableHeaderCell>
                  <TableHeaderCell>GTM / rule value</TableHeaderCell>
                  <TableHeaderCell>Source</TableHeaderCell>
                </TableRow></TableHead>
                <TableBody>{filteredParameters.map((parameter) => (
                  <TableRow key={parameter.parameterName}>
                    <TableCell><strong>{parameter.parameterName}</strong></TableCell>
                    <TableCell>{parameter.displayNames.join(", ") || <span className="ga4-config-audit__status is-dormant">Not registered</span>}</TableCell>
                    <TableCell><ChipList values={[...parameter.scopes, ...parameter.types]} /></TableCell>
                    <TableCell><ChipList values={parameter.events} empty="Property-wide / unknown" /></TableCell>
                    <TableCell><ChipList values={parameter.gtmValues} /></TableCell>
                    <TableCell><ChipList values={parameter.sources} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader split>
          <div>
            <CardTitle>Standard GA4 parameter reference</CardTitle>
            <CardDescription>Built-in and recommended parameter names available when planning GTM tags.</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowStandardParameters((current) => !current)}>
            {showStandardParameters ? "Hide reference" : `Show ${GA4_STANDARD_DIMENSION_SUGGESTIONS.length} parameters`}
          </Button>
        </CardHeader>
        {showStandardParameters && <CardContent><GenericResourceTable rows={filteredStandardParameters} /></CardContent>}
      </Card>
    </div>
  )

  const renderRules = () => (
    <div className="ui-stack ui-stack--lg">
      <Card>
        <CardHeader><CardTitle>Key events</CardTitle><CardDescription>Events marked as important business outcomes in GA4.</CardDescription></CardHeader>
        <CardContent><GenericResourceTable rows={keyEvents} /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Create event rules</CardTitle><CardDescription>GA4-side rules that create a new event from an incoming event.</CardDescription></CardHeader>
        <CardContent><GenericResourceTable rows={createRules} /></CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Modify event rules</CardTitle><CardDescription>GA4-side rules that alter incoming event names or parameters.</CardDescription></CardHeader>
        <CardContent><GenericResourceTable rows={editRules} /></CardContent>
      </Card>
    </div>
  )

  const renderLinks = () => (
    <div className="ga4-config-audit__link-grid">
      {LINK_SECTIONS.map(([key, label, collectionKey]) => {
        const rows = ga4AuditCollection(extendedAudit, collectionKey)
        return (
          <Card key={key}>
            <CardHeader split><div><CardTitle>{label}</CardTitle><CardDescription>Connected resources</CardDescription></div><Badge variant={rows.length ? "accent" : "outline"}>{rows.length}</Badge></CardHeader>
            <CardContent><GenericResourceTable rows={rows} /></CardContent>
          </Card>
        )
      })}
    </div>
  )

  const renderActiveTab = () => ({
    findings: renderFindings,
    settings: renderSettings,
    events: renderEvents,
    parameters: renderParameters,
    rules: renderRules,
    links: renderLinks,
  }[activeTab]?.() || null)

  if (propertyLoading) return <div className="page-container"><p>Loading property…</p></div>
  if (propertyError || !property) {
    return <div className="page-container"><p className="ui-error">Unable to load this GA4 property.</p></div>
  }

  return (
    <div className="page-container page-container--wide ga4-config-audit ui-stack ui-stack--lg">
      <header className="ui-page-header">
        <div className="ui-stack ui-stack--xs">
          <span className="ga4-config-audit__eyebrow">Non-automotive GA4</span>
          <h2 className="ui-page-title">GA4 Configuration Audit</h2>
          <p className="ui-subtitle">
            {property.property_name} · {property.property_id}
            {lastRunAt ? ` · Last audited ${new Date(lastRunAt).toLocaleString()}` : ""}
          </p>
        </div>
        <div className="ui-button-row">
          <Button variant="ghost" onClick={() => navigate(`/ga4-property?id=${encodeURIComponent(propertyId)}`)}>Back</Button>
          <Button variant="outline" onClick={() => navigate("/paid-media")}>Configure in GTM</Button>
          {gtmData.setup && <Button variant="outline" disabled={gtmLoading} onClick={refreshGtm}>{gtmLoading ? "Refreshing GTM…" : "Refresh GTM inventory"}</Button>}
          <Button disabled={loading} onClick={runLiveAudit}>{loading ? "Auditing…" : "Run live audit"}</Button>
        </div>
      </header>

      {error && <div className="ga4-config-audit__alert" role="alert">{error}</div>}
      <div className="ga4-config-audit__scope">
        <div><strong>GA4 property</strong><span>{normalizedPropertyId}</span></div>
        <div><strong>Audit window</strong><select value={dateRangeDays} onChange={(event) => setDateRangeDays(Number(event.target.value))}><option value={28}>28 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>365 days</option></select></div>
        <div><strong>GTM container</strong><span>{gtmData.connection ? `${gtmData.connection.container_name} (${gtmData.connection.public_id || "ID unavailable"})` : "Not linked"}</span></div>
        <div><strong>Coverage</strong><span>GA4 Admin + Data API{gtmData.setup ? " + GTM" : ""}</span></div>
      </div>

      <nav className="ga4-config-audit__tabs" aria-label="GA4 audit sections">
        {AUDIT_TABS.map((tab) => (
          <button type="button" className={activeTab === tab.id ? "is-active" : ""} onClick={() => setActiveTab(tab.id)} key={tab.id}>{tab.label}</button>
        ))}
      </nav>

      {(activeTab === "events" || activeTab === "parameters") && (
        <div className="ga4-config-audit__filters">
          <Input label={`Search ${activeTab}`} hideLabel value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={`Search ${activeTab}…`} />
          {activeTab === "events" && <select value={eventFilter} onChange={(event) => setEventFilter(event.target.value)}><option value="all">All events</option><option value="key">Key events</option><option value="gtm">Configured in GTM</option><option value="dormant">Dormant / not observed</option></select>}
        </div>
      )}

      {loading && !Object.keys(extendedAudit).length ? <Card><CardContent><p>Collecting GA4 settings, streams, events, and parameters…</p></CardContent></Card> : renderActiveTab()}

      {extendedAudit.errors?.length > 0 && (
        <Card>
          <CardHeader><CardTitle>API coverage notes</CardTitle><CardDescription>Sections Google did not return or that are unavailable for this property.</CardDescription></CardHeader>
          <CardContent><ul className="ga4-config-audit__api-errors">{extendedAudit.errors.map((entry, index) => <li key={`${entry.section}-${index}`}><strong>{formatLabel(entry.section)}</strong>: {entry.message}</li>)}</ul></CardContent>
        </Card>
      )}
    </div>
  )
}
